import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  AccountInviteType,
  AccountInvitesResponseType,
  AccountUserType,
  AccountUsersResponseType,
  CountResponseType,
  MutationType,
  UserRoleType,
} from '../../../../../shared/types';
import request from '../../../../../utils/request';
import { USERS_PER_PAGE } from './constants';
import i18n from '../../../../../i18n';

export const useGetAllUsers = ({
  callback,
  refetchOnWindowFocus = false,
  enabled = true,
}: MutationType) => {
  return useQuery(
    ['users'],
    async () => {
      const data: AccountUsersResponseType = await request(
        '/accounts/mine/users?page_size=100',
      );
      return data;
    },
    {
      onSuccess: (response) => {
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (callback) callback(false, error);
      },
      staleTime: refetchOnWindowFocus ? 10000 : 300000,
      refetchOnWindowFocus,
      enabled,
    },
  );
};

export const useGetAllUsersList = ({
  callback,
  refetchOnWindowFocus = false,
  pageToken,
}: MutationType & {
  pageToken: string;
}) => {
  const queryClient = useQueryClient();
  return useQuery(
    ['users-list'],
    async () => {
      let params = '';
      params = pageToken ? `next_token=${pageToken}` : '';
      params = Array.from(new Set(params.split('&'))).join('&') || params;
      const data: AccountUsersResponseType = await request(
        `/accounts/mine/users${
          params.length
            ? `?page_size=${USERS_PER_PAGE}&${params}`
            : `?page_size=${USERS_PER_PAGE}`
        }`,
      );
      return data;
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('users');
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (callback) callback(false, error);
      },
      staleTime: refetchOnWindowFocus ? 10000 : 300000,
      refetchOnWindowFocus,
    },
  );
};

export const useGetInviteUsers = ({
  refetchOnWindowFocus = false,
}: MutationType) => {
  return useQuery(
    'invited-users',
    async () => {
      const data: AccountInvitesResponseType = await request(
        '/accounts/mine/invites',
      );
      return data?.account_invites;
    },
    {
      staleTime: refetchOnWindowFocus ? 10000 : 300000,
      refetchOnWindowFocus,
    },
  );
};
export const useGetActiveUsersCount = () => {
  return useQuery(
    'users-count',
    async () => {
      const data: CountResponseType = await request(
        '/accounts/mine/users/count',
      );
      return data;
    },
    {
      refetchOnWindowFocus: true,
    },
  );
};

export const useUpdateUserRole = ({ setToast }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (payload: {
      userId: string;
      display_id: string;
      user_role: UserRoleType;
    }) => {
      const data = await request(
        payload.userId
          ? `/accounts/mine/users/${payload.userId}/change-role/${payload.user_role}`
          : `/accounts/mine/invites/${payload.display_id}`,
        payload.userId ? {} : payload,
        payload.userId ? 'POST' : 'PATCH',
      );
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users-list');
        queryClient.invalidateQueries('invited-users');
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('settings.toasts.user_role_changed')}`,
          });
      },
      onError: () => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('settings.toasts.user_role_change_failed')}`,
          });
      },
    },
  );
};

export const useResendAccountInvite = ({ setToast }: MutationType) => {
  return useMutation(
    async (userid: string) => {
      const data = await request(
        `/accounts/mine/invites/${userid}/resend-invitation`,
        {},
        'GET',
      );
      return data;
    },
    {
      onSuccess: () => {
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('settings.toasts.resend_invite_success')}`,
          });
      },
      onError: (error: any) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text:
              error.response.status === 429
                ? `${i18n.t('settings.toasts.invite_limit')}`
                : `${i18n.t('settings.toasts.resend_invite_failed')}`,
          });
      },
    },
  );
};

export const useResendAccountActivation = ({ setToast }: MutationType) => {
  return useMutation(
    async (userid: string) => {
      const data = await request(
        `/accounts/mine/users/${userid}/resend-activation`,
        {},
        'GET',
      );
      return data;
    },
    {
      onSuccess: () => {
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('settings.toasts.resend_user_activation_success')}`,
          });
      },
      onError: () => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('settings.toasts.resend_user_activation_failed')}`,
          });
      },
    },
  );
};

export type UpdateUserDetailsType = {
  message?: {
    success?: string;
    failed?: string;
  };
  userId?: string;
  isInvited?: boolean;
  payload?: AccountUserType | AccountInviteType;
};

export const useUpdateUserDetails = ({ setToast }: MutationType) => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: UpdateUserDetailsType) => {
      const response = await request(
        data.isInvited
          ? `/accounts/mine/invites/${data.userId}`
          : `/accounts/mine/users/${data.userId}`,
        data.payload,
        'PATCH',
      );
      return response;
    },
    {
      onSuccess: (response, data) => {
        queryClient.invalidateQueries(
          data.isInvited ? 'invited-users' : 'users-list',
        );
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text:
              data?.message?.success ||
              `${i18n.t('settings.toasts.update_user_success')}`,
          });
      },
      onError: (_, data) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text:
              data?.message?.failed ||
              `${i18n.t('settings.toasts.update_user_failed')}`,
          });
      },
    },
  );
};

export const useInviteUsers = ({ setToast, callback }) => {
  const queryClient = useQueryClient();

  return useMutation(
    async (users: AccountInvitesResponseType) => {
      const data: AccountInvitesResponseType = await request(
        '/accounts/mine/invites',
        users,
        'POST',
      );
      return data;
    },
    {
      onSuccess: () => {
        if (callback) callback(true);
        queryClient.invalidateQueries('invited-users');
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('settings.toasts.invite_user_success')}`,
          });
      },
      onError: (error: any) => {
        if (callback) callback(false, error);
        let toastMessage;
        // if message is coming from backend then showing that
        // Else we are checking code and showing toast. Else we show a generic toast
        if (error?.response?.data) {
          toastMessage = error?.response?.data;
        } else {
          toastMessage =
            error?.response?.status == 404
              ? `${i18n.t('settings.toasts.invite_existing_user')}`
              : error?.response?.status == 429
              ? `${i18n.t('settings.toasts.invite_limit')}`
              : `${i18n.t('settings.toasts.invite_user_failed')}`;
        }
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: toastMessage,
          });
      },
    },
  );
};

export const useDeleteUser = ({ setToast }) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (data: UpdateUserDetailsType) => {
      const res = await request(
        data.isInvited
          ? `/accounts/mine/invites/${data.userId}`
          : `/accounts/mine/users/${data.userId}`,
        {},
        'DELETE',
      );
      return res;
    },
    {
      onSuccess: (response, data) => {
        queryClient.invalidateQueries(
          data.isInvited ? 'invited-users' : 'users-list',
        );
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('settings.toasts.delete_user_success')}`,
          });
      },
      onError: (error: any) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('settings.toasts.delete_user_failed')}`,
          });
      },
    },
  );
};
